// Библиотека документов: фильтр по типам, загрузка, скачивание, поиск.
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Library, Upload, Search, Download, FileText, Trash2, FolderOpen } from 'lucide-react';
import { libraryApi, objectsApi, getErrorMessage } from '@/api';
import { AppLayout, PageHeader } from '@/components/layout/AppLayout';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/hooks/useAuth';
import { formatDate } from '@/lib/format';
import type { LibraryDocType } from '@/types';

const DOC_TYPES: { key: LibraryDocType | 'all'; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'defect_list', label: 'Дефектные ведомости' },
  { key: 'survey_act', label: 'Акты обследования' },
  { key: 'project', label: 'Проектная документация' },
  { key: 'estimate', label: 'Сметы' },
  { key: 'expertise', label: 'Экспертизы' },
  { key: 'reference', label: 'Образцы' },
  { key: 'snip_gost', label: 'СНиП / ГОСТ' },
  { key: 'letter', label: 'Письма' },
  { key: 'other', label: 'Прочее' },
];
const UPLOAD_TYPES = DOC_TYPES.filter((t) => t.key !== 'all') as { key: LibraryDocType; label: string }[];

export function LibraryPage() {
  const { can } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();
  const [docType, setDocType] = useState<LibraryDocType | 'all'>('all');
  const [search, setSearch] = useState('');
  const [uploadOpen, setUploadOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['library', { docType, search }],
    queryFn: () => libraryApi.list({
      ...(docType !== 'all' ? { doc_type: docType } : {}),
      ...(search ? { search } : {}),
    }),
  });

  return (
    <AppLayout>
      <PageHeader title="Библиотека документов" subtitle={data ? `${data.count} документов` : undefined}
        actions={can('library', 'add') && (
          <Button variant="primary" onClick={() => setUploadOpen(true)}><Upload size={16} /> Загрузить</Button>
        )} />

      <div style={{ position: 'relative', marginBottom: 14, maxWidth: 420 }}>
        <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
        <input className="field__input" style={{ paddingLeft: 36 }} value={search}
          onChange={(e) => setSearch(e.target.value)} placeholder="Поиск по названию…" />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 18 }}>
        {DOC_TYPES.map((t) => (
          <button key={t.key} onClick={() => setDocType(t.key)}
            style={{
              padding: '6px 14px', borderRadius: 16, fontSize: 13, cursor: 'pointer',
              border: '1px solid var(--border-bright)',
              background: docType === t.key ? 'var(--accent)' : 'transparent',
              color: docType === t.key ? '#1a1205' : 'var(--text-secondary)',
              fontWeight: docType === t.key ? 500 : 400,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? <Spinner label="Загрузка…" /> :
        !data || data.results.length === 0 ? (
          <EmptyState icon={<Library size={32} />} title="Документов нет"
            hint={search || docType !== 'all' ? 'По этому фильтру ничего не найдено' : 'Загрузите первый документ'} />
        ) : (
          <div className="card-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {data.results.map((doc) => (
              <div key={doc.id} className="card">
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-sm)', background: 'var(--accent-glow)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <FileText size={20} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500, fontSize: 14, wordBreak: 'break-word' }}>{doc.title}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{doc.doc_type_display}</div>
                    {doc.object_address && (
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <FolderOpen size={12} /> {doc.object_address}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{formatDate(doc.created_at)}</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <a href={doc.file} target="_blank" rel="noreferrer" title="Скачать"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--accent)', fontSize: 13 }}>
                      <Download size={15} /> Скачать
                    </a>
                    {can('library', 'delete') && (
                      <DeleteButton id={doc.id} onDone={() => { qc.invalidateQueries({ queryKey: ['library'] }); toast('Удалено', 'success'); }} />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)}
        onUploaded={() => { qc.invalidateQueries({ queryKey: ['library'] }); toast('Документ загружен', 'success'); }}
        onError={(m) => toast(m, 'error')} />
    </AppLayout>
  );
}

function DeleteButton({ id, onDone }: { id: number; onDone: () => void }) {
  const m = useMutation({ mutationFn: () => libraryApi.remove(id), onSuccess: onDone });
  return (
    <button onClick={() => m.mutate()} disabled={m.isPending} title="Удалить"
      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'inline-flex' }}>
      <Trash2 size={15} />
    </button>
  );
}

function UploadModal({ open, onClose, onUploaded, onError }: {
  open: boolean; onClose: () => void; onUploaded: () => void; onError: (m: string) => void;
}) {
  const [title, setTitle] = useState('');
  const [docType, setDocType] = useState<LibraryDocType>('defect_list');
  const [object, setObject] = useState<number | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const { data: objects } = useQuery({ queryKey: ['objects-all'], queryFn: () => objectsApi.list({}), enabled: open });

  const m = useMutation({
    mutationFn: () => libraryApi.upload({ title, doc_type: docType, object: object || null }, file as File),
    onSuccess: () => { onClose(); setTitle(''); setFile(null); onUploaded(); },
    onError: (e) => onError(getErrorMessage(e)),
  });

  const pickFile = (f: File | null) => {
    setFile(f);
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  return (
    <Modal open={open} onClose={onClose} title="Загрузить документ"
      footer={<>
        <Button onClick={onClose}>Отмена</Button>
        <Button variant="primary" disabled={!title || !file || m.isPending} onClick={() => m.mutate()}>
          {m.isPending ? 'Загрузка…' : 'Загрузить'}
        </Button>
      </>}>
      <div className="field">
        <label className="field__label">Файл</label>
        <input type="file" className="field__input" onChange={(e) => pickFile(e.target.files?.[0] ?? null)} />
      </div>
      <div className="field">
        <label className="field__label">Название</label>
        <input className="field__input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Дефектная ведомость — Добролюбова 56" />
      </div>
      <div className="field">
        <label className="field__label">Тип документа</label>
        <select className="field__select" value={docType} onChange={(e) => setDocType(e.target.value as LibraryDocType)}>
          {UPLOAD_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>
      <div className="field">
        <label className="field__label">Объект (необязательно)</label>
        <select className="field__select" value={object} onChange={(e) => setObject(e.target.value ? Number(e.target.value) : '')}>
          <option value="">— не привязан —</option>
          {objects?.results.map((o) => <option key={o.id} value={o.id}>{o.address}</option>)}
        </select>
      </div>
    </Modal>
  );
}
